import React from "react";
import Paper from "@mui/material/Paper";
import Switch from "@mui/material/Switch";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import { AxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import { TooltipTableHeadComponent } from "../../../common/TableHead";
import { createApi } from "../../../utils/createApi";
import { markSiteUnsynced } from "../../../utils/syncStore";
import { universalAPIErrorHandler } from "../../../utils/universalAPIErrorHandler";
import {
  DoubleTakeSiteFields,
  DoubleTakeState,
  buildDoubleTakePatches,
  canToggleDoubleTake,
} from "./SiteAboutPageUtils/siteDoubleTake";

export const DOUBLE_TAKE_DESCRIPTION =
  "Runs a second AI check on alerts from this site's cameras and labels each one Likely FP or Likely TP in Live Alerts.";
export const DOUBLE_TAKE_OFF_HELP =
  "Turning this on enables the DoubleTake column on Edit Camera › Products so cameras can be opted in. Applies after sync.";
export const DOUBLE_TAKE_ON_HELP =
  "Cameras opt in on Edit Camera › Products › DoubleTake. Applies after sync.";
export const DOUBLE_TAKE_NO_ACCESS_HELP =
  "You can turn DoubleTake off. To turn it back on, contact support.";
export const FP_GATING_HELP =
  "Alerts marked as likely false positives are held from Immix, Patriot and Sentinel.";
export const FP_GATING_READ_ONLY_HELP =
  "Set by Actuate. Contact support to change.";
export const DOUBLE_TAKE_SAVE_ERROR =
  "DoubleTake settings couldn't be saved. Try again.";

// Matches the General Info table so the two tabs line up column for column.
const COLUMN_COUNT = 7;
const CELL_WIDTH = `${100 / COLUMN_COUNT}%`;
const CELL_SX = { fontFamily: "Mulish", width: CELL_WIDTH };

export type DoubleTakeSaveResult = "unchanged" | "saved" | "failed";

/**
 * Site About → DoubleTake (ENG-2611): the site's DoubleTake switch and, while
 * it's on, the FP gating switch. Follows the page's Edit Information / Save
 * Changes / Cancel flow: SiteAbout's Save Changes runs the save registered on
 * `saveRef`, and leaving edit mode drops any unsaved draft. A save marks the
 * site unsynced; nothing reaches the connector until the operator syncs.
 */
export const DoubleTakeTab = ({
  siteId,
  siteInfo,
  isStaff,
  editMode,
  saveRef,
  onSiteInfoSaved,
}: {
  siteId: string | number;
  siteInfo: DoubleTakeSiteFields;
  isStaff: boolean;
  editMode: boolean;
  saveRef: React.MutableRefObject<(() => Promise<DoubleTakeSaveResult>) | null>;
  onSiteInfoSaved: (fields: { vlm_enabled: boolean }) => void;
}) => {
  const navigate = useNavigate();
  const [saved, setSaved] = React.useState<DoubleTakeState>({
    vlmEnabled: Boolean(siteInfo.vlm_enabled),
    gateEnabled: false,
  });
  const [draft, setDraft] = React.useState<DoubleTakeState>(saved);
  // The gate lives on customer/{id}/, not About; until it loads the gating
  // cell shows "--" so it never shows a guessed value.
  const [gateLoaded, setGateLoaded] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const loadGate = React.useCallback(() => {
    createApi(`customer/${siteId}/`)
      .get("")
      .then((response) => {
        const gateEnabled = Boolean(response.data?.vlm_gate_enabled);
        setSaved((previous) => ({ ...previous, gateEnabled }));
        setDraft((previous) => ({ ...previous, gateEnabled }));
        setGateLoaded(true);
      })
      .catch((reason: AxiosError) => {
        universalAPIErrorHandler(reason, navigate, loadGate, () => {
          setGateLoaded(false);
        });
      });
  }, [siteId, navigate]);

  React.useEffect(() => {
    loadGate();
  }, [loadGate]);

  // Cancel (or a save) leaves edit mode; start the next edit from what's saved.
  React.useEffect(() => {
    if (!editMode) {
      setDraft(saved);
      setError(null);
    }
  }, [editMode, saved]);

  const patches = buildDoubleTakePatches(saved, draft, isStaff);
  const doubleTakeToggleable = canToggleDoubleTake(
    saved.vlmEnabled,
    siteInfo,
    isStaff
  );
  const noAccessButOn =
    !isStaff && saved.vlmEnabled && !siteInfo.doubletake_available;

  const save = (): Promise<DoubleTakeSaveResult> => {
    if (!patches.about && !patches.gate) {
      return Promise.resolve("unchanged");
    }
    setError(null);
    const requests: Promise<unknown>[] = [];
    if (patches.about) {
      requests.push(
        createApi(`customer/${siteId}/about/`).patch("", patches.about)
      );
    }
    if (patches.gate) {
      requests.push(createApi(`customer/${siteId}/`).patch("", patches.gate));
    }
    return Promise.all(requests)
      .then((): DoubleTakeSaveResult => {
        setSaved(draft);
        if (patches.about) {
          onSiteInfoSaved({ vlm_enabled: draft.vlmEnabled });
        }
        markSiteUnsynced(String(siteId));
        return "saved";
      })
      .catch(
        (reason: AxiosError) =>
          new Promise<DoubleTakeSaveResult>((resolve) => {
            universalAPIErrorHandler(
              reason,
              navigate,
              () => resolve(save()),
              () => {
                setError(DOUBLE_TAKE_SAVE_ERROR);
                resolve("failed");
              }
            );
          })
      );
  };
  saveRef.current = save;

  const titles = [
    ["DoubleTake", "doubleTake"],
    ["FP Gating", "fpGating"],
    ...Array.from({ length: COLUMN_COUNT - 2 }, () => ["", ""]),
  ];
  const toolTipText = {
    doubleTake: `${DOUBLE_TAKE_DESCRIPTION} ${
      draft.vlmEnabled ? DOUBLE_TAKE_ON_HELP : DOUBLE_TAKE_OFF_HELP
    }`,
    fpGating: isStaff
      ? FP_GATING_HELP
      : `${FP_GATING_HELP} ${FP_GATING_READ_ONLY_HELP}`,
  };

  return (
    <div data-testid="double-take-tab">
      <TableContainer component={Paper} sx={{ width: "100%" }}>
        <Table
          sx={{ width: "100%", fontFamily: "Mulish", tableLayout: "fixed" }}
        >
          <TooltipTableHeadComponent
            titlesArray={titles}
            toolTipText={toolTipText}
          />
          <TableBody>
            <TableRow>
              <TableCell align="left" sx={CELL_SX}>
                <Switch
                  checked={draft.vlmEnabled}
                  disabled={!editMode || !doubleTakeToggleable}
                  color="warning"
                  inputProps={{ "aria-label": "DoubleTake" }}
                  onChange={(event) => {
                    const vlmEnabled = event.target.checked;
                    setDraft((previous) => ({ ...previous, vlmEnabled }));
                  }}
                />
              </TableCell>
              <TableCell
                align="left"
                sx={CELL_SX}
                data-testid="double-take-gating-cell"
              >
                {draft.vlmEnabled && gateLoaded ? (
                  <Switch
                    checked={draft.gateEnabled}
                    disabled={!editMode || !isStaff}
                    color="warning"
                    inputProps={{ "aria-label": "FP gating" }}
                    onChange={(event) => {
                      const gateEnabled = event.target.checked;
                      setDraft((previous) => ({ ...previous, gateEnabled }));
                    }}
                  />
                ) : (
                  "--"
                )}
              </TableCell>
              <TableCell
                align="left"
                colSpan={COLUMN_COUNT - 2}
                sx={{ fontFamily: "Mulish", color: "#6b7280" }}
              >
                {error ? (
                  <span role="alert" className="text-red-700">
                    {error}
                  </span>
                ) : (
                  editMode && noAccessButOn && DOUBLE_TAKE_NO_ACCESS_HELP
                )}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};
